import { GestureItem } from "@tento-ui/components/calendar/calendar-gesture";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import type { DateTime, Utc } from "@tento-chrono";
import type { DateFragment } from "@tento-chrono";
import type { Option, RowLocalId } from "@tento-lona/sheets";
import type { SheetRow } from "@tento-lona";

/**
 * Context passed from gesture handlers to item action methods.
 *
 * `row` is baked into the context at gesture-invocation time by the
 * sheet-infrastructure layer (the plugin that registered the
 * gesture). The gesture site converts its `Joined.Handle` to a basic
 * `SheetRow.Handle` via `.asClient()` before baking — actions know
 * only the basic Client shape. Actions never reach for ambient sheet
 * references or accept threaded `events: EventsClient` parameters —
 * see `sdk-row-index-factory-retirement` project docs (Option 1B).
 *
 * Optional for now during the migration; required once every
 * gesture-invocation site has been updated.
 */
export interface ItemActionContext {
  readonly rowId: RowLocalId;
  readonly column: ColumnIndex.Key.Resolved.WithSubindex;
  readonly windowed: DateFragment.Windowed;
  readonly row?: SheetRow.Joined.Handle;
  /**
   * Pointer's horizontal fraction (0..1) across the day column at the
   * create gesture. Renderers that store a horizontal position
   * (stickers) read it; others ignore it. Null when the gesture didn't
   * carry it.
   */
  readonly xFraction?: number;
  /**
   * Opaque create-group keys available in the gesture's column,
   * deduped in subcolumn order (one entry per key no matter how many
   * rows declare it). Keys are declared by the producing plugins via
   * `CalendarSubcolumn.createKey`; the layout layer never interprets
   * them — consumers in the app layer map keys to their create flows.
   */
  readonly createKeys?: readonly string[];
}

/**
 * Per-gesture item surfaces. The shared gesture classes
 * (`ExistingEventGestures.Move` / `Resize` / `ClickToOpenDetails` /
 * `ContextMenu`) are generic over exactly the surface each needs, and
 * are instantiated PER RENDERER with a type guard for that renderer's
 * own item class — a renderer declares the gestures it supports by
 * composing instances, not by implementing a fat base class. There is
 * no shared item hierarchy: each renderer's item is a plain class
 * extending the `GestureItem` marker and implementing only the
 * surfaces its declared gestures require.
 */

/**
 * Surface the Move/Resize gestures need. Also exactly what the shared
 * ghost painter (`RendererGestureHost.ghost.update` → pending-cell
 * render) reads off an in-flight item.
 */
export interface Movable {
  readonly id: string;
  readonly timeRange: DateTime.Range<Utc>;
  readonly title: string;
  readonly backgroundColor: string;
  /** Instance-level writability (e.g. a read-only calendar's events). */
  readonly isEditable: boolean;
  /** Persist a drag/resize. `x` is the pointer's horizontal fraction
   *  (0..1) across the drop column for renderers that store a
   *  horizontal position (stickers); null for a resize or when the
   *  gesture didn't carry it, and ignored by renderers that don't store
   *  one. Implementations return their own item type wrapping the
   *  settled state (gestures re-apply their renderer guard before
   *  re-showing details), or null when the write produced nothing to
   *  re-show. */
  persistTimeChange(
    newTimeRange: DateTime.Range<Utc>,
    x: Option<number>,
  ): Promise<Option<Movable>>;
}

/** Surface for the details popover lifecycle (ClickToOpenDetails; the
 *  Move/Resize gestures also dismiss-mid-drag and reopen-after-drop). */
export interface OpensDetails {
  showDetails(context: ItemActionContext): void;
  dismissDetails(): void;
  isDetailsRevealed(): boolean;
}

/** Surface for the context-menu gesture. */
export interface HasContextMenu {
  showContextMenu(
    context: ItemActionContext,
    offset: { x: number; y: number },
  ): void;
  dismissContextMenu(): void;
}

/**
 * Structural probe for the layout's default `editable` attribute
 * toggle (the visual resize handles). Renderer `applyToCell` hooks may
 * override the attribute; this only provides the layout-level default
 * for items that carry instance-level writability.
 */
export function isMovableItem(
  item: Option<GestureItem>,
): item is GestureItem & Movable {
  return (
    item != null &&
    "isEditable" in item &&
    "persistTimeChange" in item &&
    "timeRange" in item
  );
}
